/** Show a short run of spoken words and mark the word currently being said. */

export const SPOKEN_WORD_LIMIT_MAX = 5;
export const SPOKEN_WORD_HIGHLIGHT_DEFAULT = '#d4ff00';

export function clampSpokenWordLimit(value) {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return SPOKEN_WORD_LIMIT_MAX;
  return Math.min(SPOKEN_WORD_LIMIT_MAX, Math.max(1, n));
}

export function tokenizeSpokenWords(text) {
  return String(text || '').replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
}

function estimateWordTimes(words, duration) {
  const span = Math.max(0.05, Number(duration) || words.length * 0.35);
  const weight = words.reduce((sum, word) => sum + Math.max(1, word.length), 0);
  let cursor = 0;
  return words.map((word, index) => {
    const share = Math.max(1, word.length) / weight;
    const start = cursor;
    const end = index === words.length - 1 ? span : Math.min(span, cursor + span * share);
    cursor = end;
    return {
      word,
      start: Math.round(start * 1000) / 1000,
      end: Math.round(end * 1000) / 1000,
    };
  });
}

function timestampsMatchWords(words, wordTimestamps) {
  if (!Array.isArray(wordTimestamps) || wordTimestamps.length !== words.length) return false;
  const fromTs = wordTimestamps
    .map((row) => String(row?.word ?? row?.token ?? '').trim())
    .join(' ');
  return fromTs === words.join(' ');
}

/** Word timings for the subtitle sentence. Whisper times win when they match the text. */
export function alignSpokenWords(text, wordTimestamps, duration) {
  const words = tokenizeSpokenWords(text);
  if (!words.length) return [];
  if (timestampsMatchWords(words, wordTimestamps)) {
    return words.map((word, index) => {
      const row = wordTimestamps[index] || {};
      const start = Math.max(0, Number(row.start) || 0);
      const end = Math.max(start + 0.04, Number(row.end) || start + 0.04);
      return { word, start, end };
    });
  }
  return estimateWordTimes(words, duration);
}

export function activeSpokenWordIndex(words, clock) {
  if (!words.length) return -1;
  const t = Number(clock) || 0;
  if (t < words[0].start) return -1;
  const inside = words.findIndex((word) => t >= word.start && t < word.end);
  if (inside >= 0) return inside;
  for (let i = words.length - 1; i >= 0; i -= 1) {
    if (t >= words[i].start) return i;
  }
  return -1;
}

/** The chunk of at most `limit` words that contains the word being spoken. */
export function spokenWordWindowAt(words, clock, limit) {
  const max = clampSpokenWordLimit(limit);
  if (!words.length) return { tokens: [], text: '', activeIndex: -1 };
  const activeIndex = activeSpokenWordIndex(words, clock);
  const anchor = activeIndex < 0 ? 0 : activeIndex;
  const chunkStart = Math.floor(anchor / max) * max;
  const slice = words.slice(chunkStart, chunkStart + max);
  const tokens = slice.map((word, index) => ({
    word: word.word,
    active: activeIndex >= 0 && chunkStart + index === activeIndex,
  }));
  return {
    tokens,
    text: tokens.map((token) => token.word).join(' '),
    activeIndex,
  };
}

/** One caption cue per spoken word, each showing its chunk and the active word. */
export function expandSpokenWordCues(words, limit) {
  const max = clampSpokenWordLimit(limit);
  const cues = [];
  words.forEach((word, index) => {
    const chunkStart = Math.floor(index / max) * max;
    const chunk = words.slice(chunkStart, chunkStart + max);
    const end = index + 1 < words.length ? words[index + 1].start : word.end;
    cues.push({
      text: chunk.map((item) => item.word).join(' '),
      highlightIndex: index - chunkStart,
      startOffset: word.start,
      duration: Math.max(0.04, end - word.start),
    });
  });
  return cues;
}
